import React, { useEffect, lazy, Suspense, useRef } from 'react';
import {
    Route,
    Routes,
    useLocation,
} from 'react-router-dom';
import { fetchGetQuery } from '/src/api';
import {
    useAuthContext,
    useCartContext,
    useModalsContext,
    useWishListsContext,
    useStoreConfigContext,
} from '/src/providers';
import { Loader, useUserActivitiesAndSelectedConsents } from '/src/components';
import { COOKIE_CONSENT } from '/src/consts';
import {
    intercomLoginUser,
    intercomLogoutUser,
    hotjarIdentify,
    sentrySetUser,
    vwoVisitor,
} from '/src/services';
import { getCookie, windowScrollTo } from '/src/utils';
import { jsonSchemaStore } from '/src/json-schemas';

const Home = lazy(() => import(/* webpackChunkName: "home" */ '/src/view/Home'));
const Search = lazy(() => import(/* webpackChunkName: "search" */ '/src/view/Search'));
const Basket = lazy(() => import(/* webpackChunkName: "basket" */ '/src/view/Basket'));
const Recipes = lazy(() => import(/* webpackChunkName: "recipes" */ '/src/view/Recipes'));
const Checkout = lazy(() => import(/* webpackChunkName: "checkout" */ '/src/view/Checkout'));
const Customer = lazy(() => import(/* webpackChunkName: "customer" */ '/src/view/Customer'));
const UrlResolver = lazy(() => import(/* webpackChunkName: "url-resolver" */ '/src/view/UrlResolver'));
const WishListShared = lazy(() => import(/* webpackChunkName: "wish-list-shared" */ '/src/view/WishListShared'));
const ResetPassword = lazy(() => import(/* webpackChunkName: "reset-password" */ '/src/view/ResetPassword'));
const EmailPreferences = lazy(() => import(/* webpackChunkName: "email-preferences" */ '/src/view/EmailPreferences'));
const PlasticRecoveryFromNature = lazy(() => import(/* webpackChunkName: "plastic-recovery-from-nature" */ '/src/view/PlasticRecoveryFromNature'));
const LoginAsCustomer = lazy(() => import(/* webpackChunkName: "login-as-customer" */ '/src/view/LoginAsCustomer'));

const App = () => {
    const { storeConfig } = useStoreConfigContext();
    const { isAuth } = useAuthContext();
    const {
        cart,
        isCart,
        getCustomerCart,
        getCart,
        clearCart,
        isCartItems,
        getLocalCartId,
        removeLocalCartId,
    } = useCartContext();
    const { pathname, search } = useLocation();
    const { showModal, MODALS_TYPES } = useModalsContext();
    const { isWishLists, clearWishLists, getWishLists } = useWishListsContext();
    const ref = useRef(isAuth);

    useUserActivitiesAndSelectedConsents();

    useEffect(() => {
        windowScrollTo();
    }, [pathname, search]);

    useEffect(() => {
        if (isAuth) {
            const guestCart = cart;

            getCustomerCart()
                .then(({ customerCart: { is_cart_merged } }) => {
                    if (getLocalCartId()) {
                        removeLocalCartId();
                    }

                    if (guestCart.id && isCartItems && !is_cart_merged) {
                        showModal(MODALS_TYPES.MERGE_CARTS, { guestCart })
                    }
                }).catch(() => {});

            getWishLists().catch(() => {});
        } else {
            if (isCart) {
                clearCart();
            } else if (getLocalCartId()) {
                getCart(getLocalCartId())
                    .catch(() => removeLocalCartId());
            }

            if (isWishLists) {
                clearWishLists();
            }
        }
    }, [isAuth]);

    useEffect(() => {
        if (isAuth) {
            fetchGetQuery({
                query: `{ customer { email firstname lastname } }`,
            }).then(({ customer: { email, firstname, lastname } }) => {
                sentrySetUser({ email });
                hotjarIdentify(email, `${firstname} ${lastname}`);
                vwoVisitor(email);

                if (ref.current) {
                    intercomLoginUser({ email, name: `${firstname} ${lastname}` });
                }
            }).catch(() => {});
        } else {
            if (ref.current !== isAuth) {
                intercomLogoutUser();
            }
        }

        ref.current = isAuth;
    }, [isAuth]);

    useEffect(() => {
        if (!getCookie(COOKIE_CONSENT)) {
            showModal(MODALS_TYPES.COOKIE_CONSENT);
        }

        jsonSchemaStore(storeConfig);
    }, []);

    return (
        <Suspense fallback={<Loader />}>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/search" element={<Search />} />
                <Route path="/basket" element={<Basket />} />
                <Route path="/recipes" element={<Recipes />} />
                <Route path="/checkout/*" element={<Checkout />} />
                <Route path="/customer/*" element={<Customer />} />
                <Route path="/wish-list/shared/:id" element={<WishListShared />} />
                <Route path="/customer/account/createPassword" element={<ResetPassword />} />
                <Route path="/emailpreferences/edit" element={<EmailPreferences />} />
                <Route path="/plastic-recovery-from-nature" element={<PlasticRecoveryFromNature />} />
                <Route path="/login-as-customer" element={<LoginAsCustomer />} />
                <Route path="*" element={<UrlResolver />} />
            </Routes>
        </Suspense>
    )
};

export default App;
